import React, { useMemo, useState } from 'react';
import type {
  WorkflowCoordinatorAgent,
  WorkflowFrontAgent,
  WorkflowStateAgents,
  WorkflowValidatorAgent,
  WorkflowWorkerAgent,
} from '../../../types/workflow';
import {
  blankFrontAgent,
  blankStateAgents,
  blankValidatorAgent,
  blankWorkerAgent,
  isSpecialWorker,
  modelsInUse,
  parseCoordinatorAgent,
  parseStateAgents,
} from '../../../utils/workflowAgents';
import { Banner, ChipsInput, Field, FieldGroup, Pill } from './WorkflowFormControls';

export interface WorkflowAgentsEditorProps {
  /** `state.agents`, straight off the snapshot — untyped, per docs/WORKFLOW_API.md. */
  value: unknown;
  onChange: (next: unknown) => void;
}

const TOOL_FLAGS: Array<{ key: keyof WorkflowCoordinatorAgent; label: string }> = [
  { key: 'use_rag', label: 'RAG' },
  { key: 'use_ltm', label: 'Long-term memory' },
  { key: 'use_diary_read', label: 'Diary · read' },
  { key: 'use_diary_write', label: 'Diary · write' },
  { key: 'use_portrait_read', label: 'Portrait · read' },
  { key: 'use_portrait_write', label: 'Portrait · write' },
  { key: 'use_user_message_injection_tool', label: 'User-message injection' },
];

const KNOWN_META_KEYS = [
  'CORE_PROMPT',
  'MASTER_SUFFIX',
  'ASYNC_SUFFIX',
  'SYNC_EXTRA',
  'RAG_PROMPT',
  'LTM_PROMPT',
  'MODERATION_PROMPT',
  'DIARY_WRITE_PROMPT',
  'DIARY_READ_PROMPT',
  'PORTRAIT_READ_PROMPT',
  'PORTRAIT_WRITE_PROMPT',
  'FINAL_STEP_PROMPT',
];

// ─── A single collapsible agent card (coordinator / front / worker) ────────

const AgentCardShell: React.FC<{
  title: React.ReactNode;
  subtitle?: string;
  onRemove?: () => void;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, onRemove, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cwf-agent-card">
      <div className="cwf-agent-card__head">
        <button type="button" className="cwf-agent-card__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <i className={`fas ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
          <span className="cwf-agent-card__title">{title}</span>
          {!open && subtitle ? <span className="cwf-section__summary">{subtitle}</span> : null}
        </button>
        {onRemove ? (
          <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--danger" onClick={onRemove} title="Remove this agent">
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {open ? <div className="cwf-agent-card__body">{children}</div> : null}
    </div>
  );
};

interface AgentFieldsProps<T extends Record<string, unknown>> {
  agent: T;
  onChange: (patch: Partial<T>) => void;
  modelSuggestions: string[];
  showTools: boolean;
}

/** Fields shared by coordinator / front / worker: name, model, prompt, temperature, reminder, tools. */
function AgentFields<T extends WorkflowFrontAgent | WorkflowCoordinatorAgent | WorkflowWorkerAgent>({
  agent,
  onChange,
  modelSuggestions,
  showTools,
}: AgentFieldsProps<T>) {
  const tools = agent as unknown as WorkflowCoordinatorAgent;
  return (
    <>
      <div className="cwf-grid-2">
        <Field label="name">
          <input className="cwf-input" value={agent.name as string} onChange={(e) => onChange({ name: e.target.value } as Partial<T>)} />
        </Field>
        <Field label="model">
          <input
            className="cwf-input cwf-input--mono"
            list="cwf-agent-models"
            value={agent.model as string}
            onChange={(e) => onChange({ model: e.target.value } as Partial<T>)}
          />
        </Field>
      </div>
      <Field label="description">
        <input
          className="cwf-input"
          value={(agent.description as string) || ''}
          onChange={(e) => onChange({ description: e.target.value || undefined } as Partial<T>)}
        />
      </Field>
      <Field label="prompt">
        <textarea
          className="cwf-textarea"
          rows={6}
          value={agent.prompt as string}
          onChange={(e) => onChange({ prompt: e.target.value } as Partial<T>)}
        />
      </Field>
      <div className="cwf-grid-2">
        <Field label="temperature">
          <input
            type="number"
            className="cwf-input"
            min={0}
            max={2}
            step={0.1}
            value={agent.temperature as number}
            onChange={(e) => onChange({ temperature: Number(e.target.value) } as Partial<T>)}
          />
        </Field>
        <Field label="models_reasoning" hint="GPT-5 family reasoning effort — leave empty otherwise.">
          <input
            className="cwf-input"
            value={(agent.models_reasoning as string) || ''}
            onChange={(e) => onChange({ models_reasoning: e.target.value || undefined } as Partial<T>)}
          />
        </Field>
      </div>
      <Field label="reminder_prompt" hint="Optional — injected alongside the main prompt.">
        <textarea
          className="cwf-textarea"
          rows={2}
          value={(agent.reminder_prompt as string) || ''}
          onChange={(e) => onChange({ reminder_prompt: e.target.value || undefined } as Partial<T>)}
        />
      </Field>
      {showTools ? (
        <div className="cwf-field">
          <span className="cwf-field__label">Tools</span>
          <div className="cwf-agent-tools">
            {TOOL_FLAGS.map(({ key, label }) => (
              <label key={String(key)} className="cwf-check cwf-check--sm">
                <input
                  type="checkbox"
                  checked={Boolean(tools[key])}
                  onChange={(e) => onChange({ [key]: e.target.checked || undefined } as Partial<T>)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {tools.use_rag ? (
            <FieldGroup label="rag_doc_ids" hint="Empty — every indexed document is eligible.">
              <ChipsInput
                value={tools.rag_doc_ids || []}
                onChange={(next) => onChange({ rag_doc_ids: next.length ? next : undefined } as Partial<T>)}
                placeholder="document id"
              />
            </FieldGroup>
          ) : null}
          <FieldGroup label="state_machine_tools">
            <ChipsInput
              value={tools.state_machine_tools || []}
              onChange={(next) => onChange({ state_machine_tools: next.length ? next : undefined } as Partial<T>)}
              placeholder="tool name"
            />
          </FieldGroup>
        </div>
      ) : null}
      <datalist id="cwf-agent-models">
        {modelSuggestions.map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>
    </>
  );
}

const ValidatorCard: React.FC<{
  validator: WorkflowValidatorAgent;
  onChange: (patch: Partial<WorkflowValidatorAgent>) => void;
  onRemove: () => void;
  modelSuggestions: string[];
}> = ({ validator, onChange, onRemove, modelSuggestions }) => (
  <AgentCardShell title="Validator" subtitle={validator.model} onRemove={onRemove}>
    <div className="cwf-grid-2">
      <Field label="name">
        <input className="cwf-input" value={validator.name} onChange={(e) => onChange({ name: e.target.value })} />
      </Field>
      <Field label="model">
        <input className="cwf-input cwf-input--mono" list="cwf-agent-models" value={validator.model} onChange={(e) => onChange({ model: e.target.value })} />
      </Field>
    </div>
    <FieldGroup label="rules" hint="One check per rule — the validator retries the agent's reply until every rule passes or max_retries is hit.">
      <ChipsInput value={validator.rules} onChange={(next) => onChange({ rules: next })} placeholder="rule" />
    </FieldGroup>
    <div className="cwf-grid-2">
      <Field label="max_retries">
        <input
          type="number"
          className="cwf-input"
          min={0}
          value={validator.max_retries}
          onChange={(e) => onChange({ max_retries: Number(e.target.value) })}
        />
      </Field>
      <Field label="max_length" hint="Optional character cap.">
        <input
          type="number"
          className="cwf-input"
          min={0}
          value={validator.max_length ?? ''}
          onChange={(e) => onChange({ max_length: e.target.value ? Number(e.target.value) : undefined })}
        />
      </Field>
    </div>
    <div className="cwf-inline">
      <label className="cwf-check">
        <input type="checkbox" checked={validator.enabled !== false} onChange={(e) => onChange({ enabled: e.target.checked })} />
        <span>enabled</span>
      </label>
      <label className="cwf-check">
        <input type="checkbox" checked={Boolean(validator.alliance_check)} onChange={(e) => onChange({ alliance_check: e.target.checked || undefined })} />
        <span>alliance_check</span>
      </label>
    </div>
    {validator.alliance_check ? (
      <Field label="alliance_check_prompt">
        <textarea
          className="cwf-textarea"
          rows={3}
          value={validator.alliance_check_prompt || ''}
          onChange={(e) => onChange({ alliance_check_prompt: e.target.value || undefined })}
        />
      </Field>
    ) : null}
    <div className="cwf-grid-2">
      <Field label="on_exhausted" hint="What happens once max_retries is hit.">
        <input
          className="cwf-input"
          value={validator.on_exhausted || ''}
          onChange={(e) => onChange({ on_exhausted: e.target.value || undefined })}
        />
      </Field>
      <Field label="models_reasoning">
        <input
          className="cwf-input"
          value={validator.models_reasoning || ''}
          onChange={(e) => onChange({ models_reasoning: e.target.value || undefined })}
        />
      </Field>
    </div>
    <Field label="fallback_reply" hint="Sent to the user if validation never passes.">
      <textarea
        className="cwf-textarea"
        rows={2}
        value={validator.fallback_reply || ''}
        onChange={(e) => onChange({ fallback_reply: e.target.value || undefined })}
      />
    </Field>
    <datalist id="cwf-agent-models">
      {modelSuggestions.map((m) => (
        <option key={m} value={m} />
      ))}
    </datalist>
  </AgentCardShell>
);

// ─── A worker group (sync / async / special) ───────────────────────────────

type WorkerTone = 'sync' | 'async' | 'special';

const WORKER_GROUP_META: Record<WorkerTone, { title: string; icon: string; hint?: string }> = {
  sync: { title: 'Sync agents', icon: 'fa-bolt', hint: 'Run in the turn itself — the coordinator waits for them before replying.' },
  async: { title: 'Async agents', icon: 'fa-clock', hint: 'Run alongside the reply and write their results for later turns to read.' },
  special: {
    title: 'Special agents',
    icon: 'fa-star',
    hint: 'Scoped to a state_context_key (or recognized by name) rather than being an interchangeable worker — the target system resolves them by that key.',
  },
};

const WorkerGroup: React.FC<{
  tone: WorkerTone;
  workers: WorkflowWorkerAgent[];
  modelSuggestions: string[];
  onAdd: () => void;
  onPatch: (id: string, patch: Partial<WorkflowWorkerAgent>) => void;
  onRemove: (id: string) => void;
}> = ({ tone, workers, modelSuggestions, onAdd, onPatch, onRemove }) => {
  const meta = WORKER_GROUP_META[tone];
  return (
    <div className={`cwf-worker-group cwf-worker-group--${tone}`} data-testid={`cwf-worker-group-${tone}`}>
      <div className="cwf-worker-group__head">
        <span className="cwf-worker-group__title">
          <i className={`fas ${meta.icon}`} aria-hidden="true" /> {meta.title}
        </span>
        <Pill tone="neutral">{workers.length}</Pill>
        <button type="button" className="cwf-btn cwf-btn--xs" onClick={onAdd}>
          <i className="fas fa-plus" aria-hidden="true" /> Add
        </button>
      </div>
      {meta.hint ? <p className="cwf-worker-group__hint">{meta.hint}</p> : null}
      {workers.length === 0 ? (
        <p className="cwf-worker-group__empty">No agents in this group yet.</p>
      ) : (
        <div className="cwf-worker-group__list">
          {workers.map((w) => (
            <AgentCardShell
              key={w.id}
              title={w.name || 'Worker'}
              subtitle={`${w.model}${w.enabled === false ? ' · disabled' : ''}`}
              onRemove={() => onRemove(w.id)}
              defaultOpen={false}
            >
              <AgentFields agent={w} onChange={(patch) => onPatch(w.id, patch)} modelSuggestions={modelSuggestions} showTools />
              <div className="cwf-grid-2">
                <Field label="mode">
                  <select
                    className="cwf-input"
                    value={w.mode || 'synchronous'}
                    onChange={(e) => onPatch(w.id, { mode: e.target.value as 'synchronous' | 'asynchronous' })}
                  >
                    <option value="synchronous">synchronous</option>
                    <option value="asynchronous">asynchronous</option>
                  </select>
                </Field>
                <Field label="enabled" hint="Disabled keeps the config but the backend ignores this worker here.">
                  <label className="cwf-check">
                    <input type="checkbox" checked={w.enabled !== false} onChange={(e) => onPatch(w.id, { enabled: e.target.checked })} />
                    <span>{w.enabled === false ? 'disabled' : 'enabled'}</span>
                  </label>
                </Field>
              </div>
              <div className="cwf-grid-2">
                <Field label="state_context_key" hint="Set this (or use_state_context) to move the agent into Special.">
                  <input
                    className="cwf-input cwf-input--mono"
                    value={w.state_context_key || ''}
                    onChange={(e) => onPatch(w.id, { state_context_key: e.target.value || undefined })}
                  />
                </Field>
                <Field label="use_state_context">
                  <label className="cwf-check">
                    <input type="checkbox" checked={Boolean(w.use_state_context)} onChange={(e) => onPatch(w.id, { use_state_context: e.target.checked || undefined })} />
                    <span>reads/writes persistent state context</span>
                  </label>
                </Field>
              </div>
            </AgentCardShell>
          ))}
        </div>
      )}
    </div>
  );
};

const MetaPromptsEditor: React.FC<{
  value: Record<string, string> | undefined;
  onChange: (next: Record<string, string> | undefined) => void;
}> = ({ value, onChange }) => {
  const entries = useMemo(() => Object.entries(value || {}), [value]);

  const setKey = (index: number, nextKey: string) => {
    const next = [...entries];
    next[index] = [nextKey, next[index][1]];
    onChange(Object.fromEntries(next));
  };
  const setValue = (index: number, nextValue: string) => {
    const next = [...entries];
    next[index] = [next[index][0], nextValue];
    onChange(Object.fromEntries(next));
  };
  const remove = (index: number) => {
    const next = entries.filter((_, i) => i !== index);
    onChange(next.length ? Object.fromEntries(next) : undefined);
  };
  const add = () => {
    let key = 'NEW_PROMPT';
    let n = 1;
    const used = new Set(entries.map(([k]) => k));
    while (used.has(key)) {
      n += 1;
      key = `NEW_PROMPT_${n}`;
    }
    onChange(Object.fromEntries([...entries, [key, '']]));
  };

  return (
    <div className="cwf-meta-prompts">
      {entries.length === 0 ? <p className="cwf-note">No meta prompts for this state.</p> : null}
      {entries.map(([key, val], i) => (
        <div key={i} className="cwf-meta-prompts__row">
          <input
            className="cwf-input cwf-input--mono cwf-input--sm"
            list="cwf-meta-prompt-keys"
            value={key}
            onChange={(e) => setKey(i, e.target.value)}
          />
          <textarea className="cwf-textarea" rows={2} value={val} onChange={(e) => setValue(i, e.target.value)} />
          <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--danger" onClick={() => remove(i)} title="Remove">
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        </div>
      ))}
      <datalist id="cwf-meta-prompt-keys">
        {KNOWN_META_KEYS.map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
      <button type="button" className="cwf-btn cwf-btn--xs" onClick={add}>
        <i className="fas fa-plus" aria-hidden="true" /> Meta prompt
      </button>
    </div>
  );
};

const WorkflowAgentsEditor: React.FC<WorkflowAgentsEditorProps> = ({ value, onChange }) => {
  const parsed = useMemo(() => parseStateAgents(value), [value]);
  const modelSuggestions = useMemo(() => (parsed ? modelsInUse(parsed) : []), [parsed]);

  if (value != null && parsed === null) {
    return (
      <div className="cwf-agents">
        <Banner tone="warn" title="This agents configuration has an unsupported shape">
          <p>The current value is preserved. Replace it with a standard structured configuration to edit agents here.</p>
          <div className="cwf-banner__actions">
            <button type="button" className="cwf-btn cwf-btn--sm cwf-btn--primary" onClick={() => onChange(blankStateAgents())}>
              <i className="fas fa-rotate" aria-hidden="true" /> Use standard configuration
            </button>
          </div>
        </Banner>
      </div>
    );
  }

  if (!parsed) {
    return (
      <div className="cwf-agents cwf-agents--empty">
        <p className="cwf-note">This state has no agents configured.</p>
        <button type="button" className="cwf-btn cwf-btn--sm cwf-btn--primary" onClick={() => onChange(blankStateAgents())}>
          <i className="fas fa-robot" aria-hidden="true" /> Configure agents
        </button>
      </div>
    );
  }

  const patchTop = (patch: Partial<WorkflowStateAgents>) => {
    const next: WorkflowStateAgents = { ...parsed };
    Object.keys(patch).forEach((k) => {
      const v = (patch as Record<string, unknown>)[k];
      if (v === undefined) delete (next as Record<string, unknown>)[k];
      else (next as Record<string, unknown>)[k] = v;
    });
    onChange(next);
  };

  const patchCoordinator = (patch: Partial<WorkflowCoordinatorAgent>) =>
    patchTop({ coordinator: { ...parsed.coordinator!, ...patch } as WorkflowCoordinatorAgent });
  const patchFront = (patch: Partial<WorkflowFrontAgent>) => patchTop({ front: { ...parsed.front!, ...patch } as WorkflowFrontAgent });
  const patchValidator = (patch: Partial<WorkflowValidatorAgent>) =>
    patchTop({ validator: { ...parsed.validator!, ...patch } as WorkflowValidatorAgent });
  const workers = parsed.workers || [];
  const patchWorkerById = (id: string, patch: Partial<WorkflowWorkerAgent>) =>
    patchTop({ workers: workers.map((w) => (w.id === id ? ({ ...w, ...patch } as WorkflowWorkerAgent) : w)) });
  const removeWorkerById = (id: string) => patchTop({ workers: workers.filter((w) => w.id !== id) });
  const addWorker = (overrides: Partial<WorkflowWorkerAgent>) => patchTop({ workers: [...workers, blankWorkerAgent(overrides)] });

  const specialWorkers = workers.filter(isSpecialWorker);
  const syncWorkers = workers.filter((w) => !isSpecialWorker(w) && w.mode !== 'asynchronous');
  const asyncWorkers = workers.filter((w) => !isSpecialWorker(w) && w.mode === 'asynchronous');

  return (
    <div className="cwf-agents">
      <div className="cwf-agents__toolbar">
        <button
          type="button"
          className="cwf-btn cwf-btn--xs cwf-btn--danger"
          onClick={() => onChange(undefined)}
          title="Remove every agent from this state"
        >
          <i className="fas fa-trash" aria-hidden="true" /> Remove all agents
        </button>
      </div>

      <div className="cwf-agents__group">
        <div className="cwf-agents__group-label">Core roles</div>
        {parsed.coordinator ? (
          <AgentCardShell title={<>Coordinator <Pill tone="accent">required</Pill></>} subtitle={parsed.coordinator.model} defaultOpen>
            <AgentFields agent={parsed.coordinator} onChange={patchCoordinator} modelSuggestions={modelSuggestions} showTools />
          </AgentCardShell>
        ) : (
          <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--primary" onClick={() => patchTop({ coordinator: parseCoordinatorAgent(undefined) })}>
            <i className="fas fa-plus" aria-hidden="true" /> Add coordinator <Pill tone="warn">required to save</Pill>
          </button>
        )}

        {parsed.front ? (
          <AgentCardShell title="Front" subtitle={parsed.front.model} onRemove={() => patchTop({ front: null })}>
            <AgentFields agent={parsed.front} onChange={patchFront} modelSuggestions={modelSuggestions} showTools={false} />
          </AgentCardShell>
        ) : (
          <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => patchTop({ front: blankFrontAgent() })}>
            <i className="fas fa-plus" aria-hidden="true" /> Add front agent
          </button>
        )}

        {parsed.validator ? (
          <ValidatorCard validator={parsed.validator} onChange={patchValidator} onRemove={() => patchTop({ validator: null })} modelSuggestions={modelSuggestions} />
        ) : (
          <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => patchTop({ validator: blankValidatorAgent() })}>
            <i className="fas fa-plus" aria-hidden="true" /> Add validator
          </button>
        )}
      </div>

      <div className="cwf-agents__group">
        <div className="cwf-agents__group-label">Worker agents</div>
        <div className="cwf-worker-groups">
          <WorkerGroup
            tone="sync"
            workers={syncWorkers}
            modelSuggestions={modelSuggestions}
            onAdd={() => addWorker({ mode: 'synchronous' })}
            onPatch={patchWorkerById}
            onRemove={removeWorkerById}
          />
          <WorkerGroup
            tone="async"
            workers={asyncWorkers}
            modelSuggestions={modelSuggestions}
            onAdd={() => addWorker({ mode: 'asynchronous' })}
            onPatch={patchWorkerById}
            onRemove={removeWorkerById}
          />
        </div>
        <WorkerGroup
          tone="special"
          workers={specialWorkers}
          modelSuggestions={modelSuggestions}
          onAdd={() => addWorker({ mode: 'asynchronous', use_state_context: true })}
          onPatch={patchWorkerById}
          onRemove={removeWorkerById}
        />
      </div>

      <div className="cwf-agents__group">
        <div className="cwf-agents__group-label">Meta prompts</div>
        <AgentCardShell title="Meta prompts" subtitle={`${Object.keys(parsed.meta_prompts || {}).length}`} defaultOpen={false}>
          <MetaPromptsEditor value={parsed.meta_prompts} onChange={(next) => patchTop({ meta_prompts: next })} />
        </AgentCardShell>
      </div>
    </div>
  );
};

export default WorkflowAgentsEditor;
